import React, { useEffect, useMemo, useCallback, useRef, useState, useLayoutEffect } from 'react'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { BaseForm } from 'components'
import _ from 'lodash'

import ResponsiveBox, { Row, Col, Item, Location } from 'devextreme-react/responsive-box'
import Button from 'devextreme-react/button'
import CheckBox from 'devextreme-react/check-box'

import useResizeObserver from '@react-hook/resize-observer'

import { IxField } from 'components/forms/field'

import { Settings } from 'helpers/settings-enum'
import { settingsMenuSelector } from 'slices/ui'

import { useAppDispatch } from 'store'
import { RootState } from 'slices'
import { setFilterValues, setFilterLookupValue, setDirectLoad, setLoad, setActiveDivRef } from 'slices/xspgetzlgtlrz-slice.gen'

import { keyboardControl } from 'helpers'
import { useIxFocusElement } from 'hooks'
import { useIxOnScreen } from 'hooks/use-on-screen'

import { IxLookupProvider, useIxLookupContext } from 'contexts/lookup-context'
import {
    IxObjectLookup,
    IxTenantLookup,
} from 'components/forms/lookups'

import {
    DateInput,
} from 'components/forms'

import { setMaxLabelWidth } from 'helpers/set-max-label-width'

const _t = (s: string) => s
const labelsMap: {[k: string]: string} = {
    OBJNR: _t('xspgetzlgtlrz:field.Objektbereich'),
    LEISTUNGSZEITRAUMVON: _t('xspgetzlgtlrz:field.LEISTUNGSZEITRAUMVON'),
    PNO: _t('xspgetzlgtlrz:field.Personenbereich'),
    LEISTUNGSZEITRAUMBIS: _t('xspgetzlgtlrz:field.LEISTUNGSZEITRAUMBIS'),
}

const lookupFields: any[] = [
    'OBJNR',
    'PNO',
]


interface IAutoUpdateForm {
    itemsData: any[],
    lastFocusedElement: any,
    divRef: any
}

export const AutoUpdateForm = ({ itemsData, lastFocusedElement, divRef }: IAutoUpdateForm) => {
    const dispatch = useAppDispatch()

    const { values, setFieldValue } = useFormikContext<any>()
    const { setFieldValue: setLookupFieldValue } = useIxLookupContext()
    const activeDivRef = useSelector((state: RootState) => state.xspgetzlgtlrzSlice.activeDivRef)

    const filterValues = useSelector((state: RootState) => state.xspgetzlgtlrzSlice.filterValues)
    const filterLookupValues = useSelector((state: RootState) => state.xspgetzlgtlrzSlice.filterLookupValues)

    const prevFilterLookupValues = useRef<any>({})


    useIxFocusElement(values, itemsData, lastFocusedElement, activeDivRef, divRef)

    useEffect(() => {
        for(const [key, value] of Object.entries(filterLookupValues)) {
            if (!_.isEqual(prevFilterLookupValues.current[key], value)) {
                setLookupFieldValue(key, (value as any), false, true)
            }
        }

        prevFilterLookupValues.current = filterLookupValues
    }, [filterLookupValues, setLookupFieldValue])

    useEffect(() => {
        // dispatch(setFilterValues(values))
        const vals = Object.keys(values)
            .filter(k => !lookupFields.includes(k))
            .reduce((obj: any, key: string) => {
                obj[key] = values[key]
                return obj
            }, {})
        dispatch(setFilterValues(vals))
    }, [dispatch, values])

    useEffect(() => {
        for(const [key, value] of Object.entries(filterValues)) {
            if (lookupFields.indexOf(key) === -1) {
                setFieldValue(key, value, false)
            }
        }
    }, [filterValues, setFieldValue])

    return null
}

export type IIxXspgetzlgtlrzGridFilterContent = {
    loadingEnabled: boolean,
    visible: boolean,
}

export const IxXspgetzlgtlrzGridFilterContent = ({ loadingEnabled, visible }: IIxXspgetzlgtlrzGridFilterContent) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const { values } = useFormikContext<any>()

    const divRef = useRef<HTMLDivElement>(null)
    const [boxWidth, setBoxWidth] = useState(0)
    const onScreen = useIxOnScreen(divRef)
    const { value: formFieldType } = useSelector((state: RootState) => settingsMenuSelector(state, Settings.formFieldType))
    
    useEffect(() => {
        if (onScreen) {
            const formElement: any = divRef.current

            if (formFieldType === 'label-inside-max-field-width' || formFieldType === 'label-left-max-field-width') {
                setTimeout(() => {
                    setMaxLabelWidth(formElement)
                }, 500) 
            }
            else {
                formElement?.style.removeProperty('--max-labels-width')
            }
        }

    },[onScreen, formFieldType])

    const itemsData = useMemo(() => ([
        { name: 'OBJNR', index: 1, type: 'field' },
        { name: 'LEISTUNGSZEITRAUMVON', index: 1, type: 'field' },
        { name: 'PNO', index: 1, type: 'field' },
        { name: 'LEISTUNGSZEITRAUMBIS', index: 1, type: 'field' },
    
        { name: 'DIRECT_LOAD', index: 0, type: 'field' },
        { name: t('global:button.load'), index: 0, type: 'button' }
    ]), [t])

    const lastFocusedElement = useRef<any>(null)
    const onKeyboardControl = keyboardControl(itemsData, t, divRef, lastFocusedElement)

    const resizeObserverCb = useCallback((entry: any) => setBoxWidth(entry.contentRect.width), [])
    useResizeObserver(divRef, resizeObserverCb)

    const directLoad = useSelector((state: RootState) => state.xspgetzlgtlrzSlice.directLoad)

    const onDirectLoadChange = useCallback((e: any) => {
        dispatch(setDirectLoad(e.value))
    }, [dispatch])

    useEffect(() => {
            const filterElement: HTMLElement = (divRef.current as any).closest('.dx-drawer-panel-content') || (divRef.current as any).closest('.dx-drawer-content')
            filterElement?.addEventListener('keydown', onKeyboardControl)

            return () => {
                filterElement?.removeEventListener('keydown', onKeyboardControl)
            }
        },[onKeyboardControl])

    useEffect(() => {
        const filterElement: HTMLElement = (divRef.current as any).closest('.dx-drawer-panel-content') || (divRef.current as any).closest('.dx-drawer-content')

        const setPrevFocusedElement = (e: any) => {
            const filterButton = e.target.closest('.dx-drawer-panel-content')?.querySelector('[aria-label="filter"]')
            dispatch(setActiveDivRef(e.target.closest('.dx-drawer-content') || e.target.closest('.dx-drawer-panel-content')))
            
            if (e.target.tagName === 'DIV' && !e.target.className.includes('dropdowneditor') && !e.target.className.includes('button') && !e.target.closest('.dx-radiobutton') && filterButton) {
                setTimeout(() => {
                    e.target.closest('.dx-drawer-panel-content').querySelector('[aria-label="filter"]').focus()
                    e.target.closest('.dx-drawer-panel-content')?.querySelector('[aria-label="filter"]').classList.remove('dx-state-focused')
                }, 0)
            }
            
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'I' || e.target.className.includes('dropdowneditor')) {
                lastFocusedElement.current = e.target
                filterElement?.querySelector('.dx-state-focused.dx-radiobutton')?.classList.remove('dx-state-focused')
                filterElement?.querySelector('.dx-checkbox.dx-state-focused')?.classList.remove('dx-state-focused')
            }
        }
        filterElement?.addEventListener('mousedown', setPrevFocusedElement)

        return () => {
            filterElement?.removeEventListener('mousedown', setPrevFocusedElement)
        }
    }, [dispatch])

    useLayoutEffect(() => {
        const width = divRef.current?.getBoundingClientRect().width
        setBoxWidth(width ? width : 0)
    }, [divRef])

    const screenByWidth = useCallback((_: any) => {
        if (boxWidth < 500) {
            return 'xs'
        } else {
            return 'lg'
        }
    }, [boxWidth])

    const onLookupFieldChange = useCallback((name: string, value: any) => {
        dispatch(setFilterLookupValue({ name, value, formValue: values[name] }))
    }, [dispatch, values])

    return (
        <div ref={ divRef } style={ visible ? {} : { height: 0 } }>
            <IxLookupProvider onFieldChanged={ onLookupFieldChange }>
                <AutoUpdateForm itemsData={ itemsData } lastFocusedElement={lastFocusedElement} divRef={divRef}/>
                <ResponsiveBox screenByWidth={ screenByWidth }>
                    {/* layout goes here */}
                    <Row />
                    <Row />
                    <Row screen='xs'/>
                    <Row screen='xs'/>
                    <Row screen = 'xs'/>

                    <Col />
                    <Col screen='sm md lg'/>
                    <Col screen='md lg'/>
                    <Col screen='md lg'/>
                    <Col screen='sm md lg' ratio={ 0.5 }/>

                    <Item>
                        <Location row={ 0 } col={ 0 } colspan={ 2 } screen='md lg' />
                        <Location row={ 0 } col={ 0 } colspan={ 1 } screen='sm' />
                        <Location row={ 0 } col={ 0 } colspan={ 1 } screen='xs' />

                        <div className='item-div'>
                            <IxField
                                as={ IxObjectLookup }
                                name='OBJNR'
                                label={ t(labelsMap['OBJNR']) }
                                active={ loadingEnabled }
                            />
                        </div>
                    </Item>
                    <Item>
                        <Location row={ 0 } col={ 2 } colspan={ 2 } screen='md lg' />
                        <Location row={ 0 } col={ 1 } colspan={ 1 } screen='sm' />
                        <Location row={ 1 } col={ 0 } colspan={ 1 } screen='xs' />

                        <div className='item-div'>
                            <IxField
                                as={ DateInput }
                                name='LEISTUNGSZEITRAUMVON'
                                label={ t(labelsMap['LEISTUNGSZEITRAUMVON']) }
                                active={ loadingEnabled }
                            />
                        </div>
                    </Item>
                    <Item>
                        <Location row={ 1 } col={ 0 } colspan={ 2 } screen='md lg' />
                        <Location row={ 1 } col={ 0 } colspan={ 1 } screen='sm' />
                        <Location row={ 2 } col={ 0 } colspan={ 1 } screen='xs' />

                        <div className='item-div'>
                            <IxField
                                as={ IxTenantLookup }
                                name='PNO'
                                label={ t(labelsMap['PNO']) }
                                active={ loadingEnabled }
                            />
                        </div>
                    </Item>
                    <Item>
                        <Location row={ 1 } col={ 2 } colspan={ 2 } screen='md lg' />
                        <Location row={ 1 } col={ 1 } colspan={ 1 } screen='sm' />
                        <Location row={ 3 } col={ 0 } colspan={ 1 } screen='xs' />

                        <div className='item-div'>
                            <IxField
                                as={ DateInput }
                                name='LEISTUNGSZEITRAUMBIS'
                                label={ t(labelsMap['LEISTUNGSZEITRAUMBIS']) }
                                active={ loadingEnabled }
                            />
                        </div>
                    </Item>

                    <Item>
                        <Location row={ 0 } col={ 4 } colspan={ 1 } rowspan={ 2 } screen='md lg' />
                        <Location row={ 0 } col={ 2 } colspan={ 1 } rowspan={ 2 } screen='sm' />
                        <Location row={ 4 } col={ 0 } colspan={ 1 } screen='xs' />

                        <div className='rb-item mb-1'>
                            <div className='mb-1'>
                                <IxField
                                    as={ CheckBox }
                                    name='DIRECT_LOAD'
                                    text={ t('global:label.directLoad') }
                                    onValueChanged={ onDirectLoadChange }
                                    value={ directLoad }
                                />
                            </div>
                            <Button disabled={ directLoad } width='100%' text={t('global:button.load')} onClick={ () => dispatch(setLoad(true)) }/>
                        </div>
                    </Item>
                </ResponsiveBox>
            </IxLookupProvider>
        </div>
    )
}

export type IIxXspgetzlgtlrzGridFilter = {
    loadingEnabled?: boolean,
    visible?: boolean,
}

export const IxXspgetzlgtlrzGridFilter = ({ loadingEnabled=true, visible=true }: IIxXspgetzlgtlrzGridFilter) => {

    const formik = useMemo(() => ({
        initialValues: {
            "OBJNR": null,
            "LEISTUNGSZEITRAUMVON": "",
            "PNO": null,
            "LEISTUNGSZEITRAUMBIS": ""
        },
        enableReinitialize: true,
        jsonSchema: null,
        avjOptions: {
            coerceTypes: true,
            $data: true,
        },

    }), [])

    return (
        <BaseForm {...formik}>
            <IxXspgetzlgtlrzGridFilterContent loadingEnabled={ loadingEnabled } visible={ visible } />
        </BaseForm>
    )
}
